// Import all modules
import { config } from './core/config.js';
import { utils } from './core/utils.js';
import { eventBus } from './core/eventBus.js';
import { errorHandler } from './core/errorHandler.js';

// Components
import { Navigation } from './components/Navigation.js';
import { ThemeManager } from './components/ThemeManager.js';
import { FormValidator } from './components/FormValidator.js';
import { SkillsChart } from './components/SkillsChart.js';
import { ProjectFilter } from './components/ProjectFilter.js';
import { BlogPagination } from './components/BlogPagination.js';
import { ScrollEffects } from './components/ScrollEffects.js';

// Sections
import { Hero } from './sections/Hero.js';
import { Skills } from './sections/Skills.js';
import { Experience } from './sections/Experience.js';

// Analytics
import { analytics } from './utils/analytics.js';
import { AnalyticsEvents } from './utils/analytics-events.js';

// App class to manage all components
class App {
  constructor() {
    this.components = {};
    this.initialized = false;
  }
  
  async init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
      return;
    }
    
    try {
      // Initialize components
      this.initializeComponents();
      
      // Setup global error handling
      this.setupErrorHandling();
      
      // Setup performance monitoring
      this.setupPerformanceMonitoring();
      
      // Initialize animations
      this.initializeAnimations();
      
      // Mark as initialized
      this.initialized = true;
      eventBus.emit('app:initialized');
      
      console.log('App initialized successfully');
    } catch (error) {
      console.error('App initialization failed:', error);
    }
  }
  
  initializeComponents() {
    // Core components
    this.components.navigation = new Navigation();
    this.components.themeManager = new ThemeManager();
    this.components.scrollEffects = new ScrollEffects();

    // Hero section
    this.components.hero = new Hero();

    // Form validation
    const contactForm = document.getElementById('contactForm');
    if (contactForm) {
      this.components.formValidator = new FormValidator(contactForm);
    }

    // Skills
    this.components.skills = new Skills();

    // Skills chart (lazy loaded)
    this.lazyLoadSkillsChart();

    // Filters and pagination
    this.components.projectFilter = new ProjectFilter();
    this.components.blogPagination = new BlogPagination();

    // Experience timeline
    this.components.experience = new Experience();

    // Social dropdown
    this.setupSocialDropdown();

    // Back to top button
    this.setupBackToTop();

    // Analytics event tracking
    this.components.analyticsEvents = new AnalyticsEvents();
    this.components.analyticsEvents.listenToThemeChanges(eventBus);
  }
  
  lazyLoadSkillsChart() {
    const skillsSection = document.querySelector('#skills');
    if (!skillsSection) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.components.skillsChart) {
          this.components.skillsChart = new SkillsChart();
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(skillsSection);
  }
  
  setupSocialDropdown() {
    const dropdown = document.querySelector('.social-dropdown');
    const toggle = document.querySelector('.social-dropdown-toggle');
    if (!dropdown || !toggle) return;

    // Toggle on click
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const isActive = dropdown.classList.toggle('active');
      toggle.setAttribute('aria-expanded', isActive.toString());
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!dropdown.contains(e.target)) {
        dropdown.classList.remove('active');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && dropdown.classList.contains('active')) {
        dropdown.classList.remove('active');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });
  }
  
  setupBackToTop() {
    const backToTop = document.querySelector('.back-to-top a');
    if (!backToTop) return;
    
    // Show/hide based on scroll position
    const toggleBackToTop = utils.throttle(() => {
      const shouldShow = window.scrollY > 500;
      backToTop.parentElement.style.opacity = shouldShow ? '1' : '0';
      backToTop.parentElement.style.visibility = shouldShow ? 'visible' : 'hidden';
    }, 200);
    
    window.addEventListener('scroll', toggleBackToTop);
    toggleBackToTop(); // Initial check
    
    // Smooth scroll to top
    backToTop.addEventListener('click', (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
  
  initializeAnimations() {
    // Initialize scroll animations if GSAP is available
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
      
      // Section headers
      gsap.utils.toArray('.section-header').forEach(header => {
        gsap.from(header, {
          y: 50,
          opacity: 0,
          duration: 1,
          scrollTrigger: {
            trigger: header,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        });
      });
      
      // Research areas
      gsap.utils.toArray('.research-area').forEach((area, index) => {
        gsap.from(area, {
          y: 50,
          opacity: 0,
          duration: 0.8,
          delay: index * 0.2,
          scrollTrigger: {
            trigger: '.research-areas',
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        });
      });
    }
  }
  
  showNotification(message, type = 'info') {
    // Simple notification (you can enhance this)
    const notification = document.createElement('div');
    notification.className = `app-notification app-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 16px 24px;
      background: ${type === 'success' ? '#10B981' : '#EF4444'};
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 9999;
      animation: slideIn 0.3s ease-out;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
  
  setupErrorHandling() {
    // Initialize centralized error handler
    errorHandler.init();

    // Listen for errors to show user-friendly feedback if needed
    eventBus.on('error:occurred', (errorInfo) => {
      // Only show notification for critical errors in production
      if (errorInfo.type === 'uncaught' && process.env.NODE_ENV === 'production') {
        this.showNotification('Something went wrong. Please refresh the page.', 'error');
      }
    });
  }
  
  setupPerformanceMonitoring() {
    // Log performance metrics
    if ('performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = window.performance.timing;
          const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
          const connectTime = perfData.responseEnd - perfData.requestStart;
          const renderTime = perfData.domComplete - perfData.domLoading;
          
          console.log('Performance metrics:', {
            pageLoadTime: pageLoadTime + 'ms',
            connectTime: connectTime + 'ms',
            renderTime: renderTime + 'ms'
          });
          
          // Send to analytics
          analytics.trackEvent('page_load_time', {
            value: pageLoadTime,
            event_category: 'performance'
          });
        }, 0);
      });
    }
  }
}

// Create styles for notifications
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  @keyframes slideOut {
    from { transform: translateX(0); }
    to { transform: translateX(100%); }
  }
`;
document.head.appendChild(style);

// Initialize app
const app = new App();
app.init();

// Export for debugging/testing
window.app = app;
window.eventBus = eventBus;